import { useReveal } from "@/hooks/useReveal";

const YACHT_IMG = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/v54xdqoj_396ACBBB-A9DE-4C38-BF4E-B85E58719616.png";
const HELI_IMG = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/wq03wr14_9ECCC2CD-E15D-47F7-BCCC-DA8DD8399800.png";
const DINNER_IMG = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/mrev9ui2_photo-output.jpeg";
const EXHIBIT_IMG = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/9cfwtibl_8F09C163-76DF-4943-994A-89EEC1A73FBF.png";

const ITINERARY = [
  { time: "16:00", title: "Helicopter Arrival", text: "From Nice — direct to the maison's private terrace." },
  { time: "18:00", title: "Boutique Preview", text: "A first encounter with the Falcon Heart, behind closed doors." },
  { time: "20:30", title: "Collector Dinner", text: "An intimate table set above the harbour." },
  { time: "22:00", title: "Private Exhibition", text: "Movements and stones revealed only to those present." },
];

export default function MonacoSection({ onRequest }) {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();
  const yachtR = useReveal();
  const heliR = useReveal();
  const dinnerR = useReveal();
  const exhibitR = useReveal();

  return (
    <section
      id="monaco"
      data-testid="monaco-section"
      className="relative py-28 md:py-40 overflow-hidden grain"
      style={{
        background:
          "linear-gradient(180deg, #000 0%, #060503 35%, #0a0805 65%, #000 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="monaco-eyebrow">
            Monaco '26 · Private Experience
          </p>
          <h2
            ref={title}
            className="pd-reveal mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.06em] leading-[1.05]"
            style={{ transitionDelay: "160ms" }}
            data-testid="monaco-title"
          >
            Some experiences <br />
            aren't announced. <br />
            <span className="italic font-serif-body text-gold-gradient">They're invited.</span>
          </h2>
          <div ref={lead} className="pd-reveal mt-8" style={{ transitionDelay: "320ms" }}>
            <p className="font-light text-white/70 text-base md:text-lg leading-relaxed tracking-wide max-w-2xl mx-auto">
              For one weekend, the maison opens its doors to a small circle of collectors
              and friends — far from the public salons, above the harbour, beyond the schedule.
            </p>
          </div>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <div
            ref={yachtR}
            className="pd-reveal md:col-span-7 md:row-span-2 relative overflow-hidden border border-[var(--pd-border)] aspect-[4/3] md:aspect-auto md:min-h-[520px]"
            data-testid="monaco-yacht-tile"
          >
            <img src={YACHT_IMG} alt="Monaco yacht" className="w-full h-full object-cover transition-transform duration-[2200ms] hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <p className="eyebrow text-white/70">Arrival in Monte-Carlo</p>
              <h3 className="font-serif-display text-2xl md:text-4xl text-white mt-2 tracking-[0.08em]">Above Port Hercule</h3>
              <p className="mt-3 text-white/70 text-sm max-w-md">A weekend held entirely off-record.</p>
            </div>
          </div>

          <div
            ref={heliR}
            className="pd-reveal md:col-span-5 relative overflow-hidden border border-[var(--pd-border)] aspect-[4/3]"
            style={{ transitionDelay: "180ms" }}
            data-testid="monaco-heli-tile"
          >
            <img src={HELI_IMG} alt="Helicopter arrival" className="w-full h-full object-cover transition-transform duration-[2200ms] hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="eyebrow text-white/70">Arrival</p>
              <h3 className="font-serif-display text-xl md:text-2xl text-white mt-1 tracking-[0.08em]">By Helicopter</h3>
            </div>
          </div>

          <div
            ref={dinnerR}
            className="pd-reveal md:col-span-5 relative overflow-hidden border border-[var(--pd-border)] aspect-[4/3]"
            style={{ transitionDelay: "260ms" }}
            data-testid="monaco-dinner-tile"
          >
            <img src={DINNER_IMG} alt="Collector dinner" className="w-full h-full object-cover transition-transform duration-[2200ms] hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="eyebrow text-white/70">The Maison</p>
              <h3 className="font-serif-display text-xl md:text-2xl text-white mt-1 tracking-[0.08em]">Collector's Table</h3>
            </div>
          </div>

          <div
            ref={exhibitR}
            className="pd-reveal md:col-span-12 relative overflow-hidden border border-[var(--pd-border)] aspect-[16/7] md:aspect-[18/5]"
            style={{ transitionDelay: "340ms" }}
            data-testid="monaco-exhibit-tile"
          >
            <img src={EXHIBIT_IMG} alt="Private exhibition" className="w-full h-full object-cover transition-transform duration-[2200ms] hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-8 md:px-14 max-w-2xl">
                <p className="eyebrow text-white/70">The Reveal</p>
                <h3 className="font-serif-display text-2xl md:text-4xl text-white mt-2 tracking-[0.08em]">Private Exhibition</h3>
                <p className="mt-3 text-white/70 text-sm max-w-md">
                  Movements, stones, and stories shown only once — and only to those in the room.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          <div>
            <p className="eyebrow mb-6">The Itinerary</p>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {ITINERARY.map((it) => (
                <div key={it.time} className="flex items-baseline justify-between py-5 gap-6">
                  <span className="font-serif-display text-[var(--pd-gold)] tracking-[0.2em] text-sm md:text-base w-20">{it.time}</span>
                  <div className="flex-1">
                    <p className="font-serif-display text-lg md:text-xl text-white tracking-[0.08em]">{it.title}</p>
                    <p className="text-white/60 text-sm mt-1">{it.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end md:text-right">
            <p className="font-serif-body italic text-white/80 text-xl md:text-2xl leading-relaxed max-w-md">
              "Some visions are too ambitious to explain. You build them anyway."
            </p>
            <div className="hairline my-8" />
            <p className="text-white/60 text-sm max-w-md md:ml-auto leading-relaxed">
              Invitations to Monaco '26 are extended privately. The maison does not maintain a waiting list.
            </p>
            <button
              data-testid="monaco-request-btn"
              onClick={onRequest}
              className="btn-gold mt-10"
            >
              <span>Request Invitation</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
